import React from 'react';
import Contract from '../../../../../../../../../models/contract';
import { formatDate, isDatePassed } from '../../utils/dates';

interface ContractInfoProps {
  contract: Contract | null;
  className?: string;
}

/**
 * Composant qui affiche les informations du contrat et de la garantie
 * Utilisé dans l'étape de qualification pour évaluer l'éligibilité du dossier
 */
const ContractInfo: React.FC<ContractInfoProps> = ({ contract, className = '' }) => {
  if (!contract) {
    return (
      <div className={`tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200 ${className}`}>
        <div className="tw-p-3 tw-text-center">
          <h3 className="tw-text-lg tw-font-medium tw-text-gray-600">Informations contrat</h3>
          <p className="tw-text-gray-500">Aucune information de contrat disponible</p>
        </div>
      </div>
    );
  }

  const warranty = contract.warranty;
  const financialCap = contract.warrantyFinancialCap || (warranty?.financialCap || 0);
  const kmLimit = contract.warrantyKmLimit || (warranty?.kmLimit || 0);

  // Calcul de l'échéance du contrat
  const isContractExpired = isDatePassed(contract.dateEnd);

  return (
    <div className={`tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm ${className}`}>
      <div className="tw-p-4 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-medium tw-flex tw-items-center tw-gap-2">
          <i className="fa-solid fa-file-contract tw-text-blue-600"></i>
          Informations contrat
        </h3>
      </div>

      <div className="tw-p-4">
        {/* Validité du contrat avec indicateur visuel */}
        <div className="tw-mb-4">
          <div className={`tw-p-3 tw-rounded-lg ${isContractExpired ? 'tw-bg-red-50' : 'tw-bg-green-50'}`}>
            <div className="tw-flex tw-items-center tw-gap-2">
              {isContractExpired ? (
                <>
                  <span className="tw-text-red-600 tw-text-lg">
                    <i className="fa-solid fa-circle-exclamation"></i>
                  </span>
                  <span className="tw-font-medium tw-text-red-700">Contrat expiré</span>
                </>
              ) : (
                <>
                  <span className="tw-text-green-600 tw-text-lg">
                    <i className="fa-solid fa-circle-check"></i>
                  </span>
                  <span className="tw-font-medium tw-text-green-700">Contrat valide</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          {/* Informations du contrat */}
          <div>
            <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Détails du contrat</h4>
            <dl className="tw-space-y-2">
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Date de souscription</dt>
                <dd className="tw-font-medium tw-text-gray-800">{formatDate(contract.dateSouscription)}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Date de fin</dt>
                <dd className={`tw-font-medium ${isContractExpired ? 'tw-text-red-600' : 'tw-text-gray-800'}`}>
                  {formatDate(contract.dateEnd)}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Durée</dt>
                <dd className="tw-font-medium tw-text-gray-800">{warranty?.duration || '-'} mois</dd>
              </div>
            </dl>
          </div>

          {/* Informations de la garantie */}
          <div>
            <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Limites de garantie</h4>
            <dl className="tw-space-y-2">
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Plafond financier</dt>
                <dd className="tw-font-medium tw-text-gray-800">
                  {financialCap ? `${financialCap.toLocaleString('fr-FR')} €` : '-'}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Limite kilométrique</dt>
                <dd className="tw-font-medium tw-text-gray-800">
                  {kmLimit ? `${kmLimit.toLocaleString('fr-FR')} km` : '-'}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Garantie</dt>
                <dd className="tw-font-medium tw-text-gray-800">{warranty?.name || '-'}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Info-bulle d'aide */}
        <div className="tw-mt-4 tw-bg-blue-50 tw-p-3 tw-rounded-lg tw-text-sm tw-text-blue-700 tw-flex tw-items-start tw-gap-2">
          <div className="tw-text-blue-600 tw-mt-0.5">
            <i className="fa-solid fa-circle-info"></i>
          </div>
          <div>
            <p>Ces informations sont utilisées pour déterminer l'éligibilité du dossier à la garantie.</p>
            <p className="tw-mt-1">Vérifiez la validité du contrat et les limites applicables avant de prendre une décision.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractInfo;